import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { getCurrentAccount } from '../../lib/auth';
import MainLayout from '../../components/MainLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../../components/ui/command';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '../../components/ui/tooltip';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import {
  Fingerprint, Search, RefreshCw, Loader2, Plus, Edit, Trash2, History,
  ChevronsUpDown, Check, Info,
} from 'lucide-react';

const EMPTY = {
  identifier_number: '', account_number: '', utility_type: '', location_id: '', gl_code: '',
  address: '', city: '', state: '', country: '', postal_code: '',
};

const IDENTIFIER_TOOLTIP = `A utility's unique reference for a connection point or meter — the name varies by country:
• US: utility account / meter number
• Australia (electricity): NMI  •  Australia (gas): MIRN
• UK (electricity): MPAN  •  UK (gas): MPRN
• Ireland: MPRN  •  New Zealand (electricity): ICP`;

// Searchable account-number combobox that also allows a brand-new value.
function AccountCombobox({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const q = query.trim();
  const filtered = (q ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase())) : options).slice(0, 100);
  const showNew = q && !options.some((o) => o.toLowerCase() === q.toLowerCase());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between rounded-none mt-1 font-mono"
          data-testid="entity-account-combobox">
          <span className={value ? '' : 'text-muted-foreground font-sans'}>{value || 'Select or type an account number'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-none" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or type new..." value={query} onValueChange={setQuery}
            data-testid="entity-account-search" />
          <CommandList>
            {showNew && (
              <CommandGroup heading="New">
                <CommandItem value={q} onSelect={() => { onChange(q); setOpen(false); setQuery(''); }}
                  data-testid="entity-account-usenew">
                  <Plus className="mr-2 h-4 w-4" /> Use "{q}"
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length === 0 && !showNew && <CommandEmpty>No account numbers found.</CommandEmpty>}
            <CommandGroup heading={q ? `Matches (${filtered.length})` : 'Existing'}>
              {filtered.map((n) => (
                <CommandItem key={n} value={n} onSelect={() => { onChange(n); setOpen(false); setQuery(''); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === n ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="font-mono">{n}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function OnboardEntities() {
  const account = getCurrentAccount();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [locations, setLocations] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [confirmChange, setConfirmChange] = useState(null); // {from, to}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [auditItem, setAuditItem] = useState(null);

  const load = useCallback(() => {
    if (!account?.id) return;
    setLoading(true);
    api.get('/ubm-migration/entities', {
      params: { account_id: account.id, search: search || undefined, page, page_size: 50 },
    })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      })
      .catch(() => toast.error('Could not load identifiers'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, search, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!account?.id) return;
    api.get(`/ubm/accounts/${account.id}/locations`)
      .then(({ data }) => setLocations(data || [])).catch(() => {});
    api.get(`/ubm/accounts/${account.id}/vendor-accounts`)
      .then(({ data }) => {
        const nums = Array.from(new Set((data || []).map((v) => v.account_number).filter(Boolean)));
        setAccountNumbers(nums);
      }).catch(() => {});
  }, [account?.id]);

  const openAdd = () => { setEditing(null); setOriginal(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (e) => {
    setEditing(e.id);
    setOriginal(e);
    setForm({
      identifier_number: e.identifier_number || '',
      account_number: e.account_number || '',
      utility_type: e.utility_type || '',
      location_id: e.location_id || '',
      gl_code: e.gl_code || '',
      address: e.address || '', city: e.city || '',
      state: e.state || '', country: e.country || '', postal_code: e.postal_code || '',
    });
    setShowDialog(true);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, account_id: account.id };
      if (editing) {
        await api.patch(`/ubm-migration/entities/${editing}`, payload, { params: { account_id: account.id } });
        toast.success('Identifier updated');
      } else {
        await api.post('/ubm-migration/entities', payload, { params: { account_id: account.id } });
        toast.success('Identifier added');
      }
      setShowDialog(false);
      setConfirmChange(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save identifier');
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!form.identifier_number.trim()) { toast.error('Identifier number is required'); return; }
    // Confirm when changing an EXISTING (non-empty) account number to a different value.
    const prevAcct = (original?.account_number || '').trim();
    const nextAcct = (form.account_number || '').trim();
    if (editing && prevAcct && nextAcct !== prevAcct) {
      setConfirmChange({ from: prevAcct, to: nextAcct || '(none)' });
      return;
    }
    doSave();
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/ubm-migration/entities/${deleteTarget.id}`, { params: { account_id: account.id } });
      toast.success('Identifier deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="onboard-entities-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Fingerprint className="h-6 w-6" /> Identifiers
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" data-testid="identifier-info" className="text-muted-foreground hover:text-foreground">
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs whitespace-pre-line text-xs">{IDENTIFIER_TOOLTIP}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h1>
            <p className="text-sm text-muted-foreground">
              Utility connection-point / meter identifiers (NMI, MIRN, MPAN, MPRN, ICP, or US account/meter numbers)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={load} data-testid="entities-refresh">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button className="rounded-none" onClick={openAdd} data-testid="add-entity-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Identifier
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9 rounded-none"
              placeholder="Search identifier, utility type, GL code, address..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              data-testid="entities-search"
            />
          </div>
          <Badge variant="outline" className="rounded-none" data-testid="entities-total">{total} identifiers</Badge>
        </div>

        <div className="border rounded-none overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left border-b">
                <th className="p-3">Identifier No</th>
                <th className="p-3">Account No</th>
                <th className="p-3">Utility Type</th>
                <th className="p-3">Property</th>
                <th className="p-3">GL Code</th>
                <th className="p-3">City</th>
                <th className="p-3">State</th>
                <th className="p-3">Country</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading...
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">
                  No identifiers yet. Run an AI Migration import or click "Add Identifier" to create one.
                </td></tr>
              ) : (
                items.map((e) => (
                  <tr key={e.id} className="border-b hover:bg-muted/30" data-testid={`entity-row-${e.id}`}>
                    <td className="p-3 font-mono font-medium">{e.identifier_number || '—'}</td>
                    <td className="p-3 font-mono" data-testid={`entity-account-${e.id}`}>{e.account_number || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3">{e.utility_type
                      ? <Badge variant="outline" className="rounded-none text-xs">{e.utility_type}</Badge>
                      : <span className="text-slate-300">—</span>}</td>
                    <td className="p-3" data-testid={`entity-property-${e.id}`}>{e.property_name || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3 font-mono">{e.gl_code && e.gl_code !== 'undefined' ? e.gl_code : <span className="text-slate-300">—</span>}</td>
                    <td className="p-3">{e.city || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3">{e.state || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3">{e.country || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none"
                          onClick={() => setAuditItem(e)} title="Audit history" data-testid={`audit-entity-${e.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none"
                          onClick={() => openEdit(e)} title="Edit" data-testid={`edit-entity-${e.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-none text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(e)} title="Delete" data-testid={`delete-entity-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-none" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)} data-testid="entities-prev">Prev</Button>
            <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="rounded-none" disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)} data-testid="entities-next">Next</Button>
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="rounded-none max-w-lg" data-testid="entity-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Identifier' : 'Add Identifier'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Identifier Number *</Label>
              <Input className="rounded-none mt-1 font-mono" value={form.identifier_number}
                onChange={(e) => setForm({ ...form, identifier_number: e.target.value })}
                placeholder="NMI / MPAN / meter or service identifier" data-testid="entity-identifier-input" />
            </div>
            <div className="col-span-2">
              <Label>Account Number <span className="text-muted-foreground text-xs">(active — latest)</span></Label>
              <AccountCombobox value={form.account_number} options={accountNumbers}
                onChange={(v) => setForm({ ...form, account_number: v })} />
            </div>
            <div className="col-span-2">
              <Label>Property</Label>
              <Select value={form.location_id || '__none__'} onValueChange={(v) => setForm({ ...form, location_id: v === '__none__' ? '' : v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="entity-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Utility Type</Label>
              <Input className="rounded-none mt-1" value={form.utility_type} onChange={(e) => setForm({ ...form, utility_type: e.target.value })} placeholder="e.g., electricity" data-testid="entity-utility-type" />
            </div>
            <div>
              <Label>GL Code</Label>
              <Input className="rounded-none mt-1 font-mono" value={form.gl_code} onChange={(e) => setForm({ ...form, gl_code: e.target.value })} data-testid="entity-gl-code" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input className="rounded-none mt-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="entity-address" />
            </div>
            <div>
              <Label>City</Label>
              <Input className="rounded-none mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="entity-city" />
            </div>
            <div>
              <Label>State</Label>
              <Input className="rounded-none mt-1" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="entity-state" />
            </div>
            <div>
              <Label>Country</Label>
              <Input className="rounded-none mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="entity-country" />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input className="rounded-none mt-1" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="entity-postal-code" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={save} disabled={saving} data-testid="save-entity-btn">
              {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Add Identifier')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm account-number change */}
      <AlertDialog open={!!confirmChange} onOpenChange={(o) => !o && setConfirmChange(null)}>
        <AlertDialogContent className="rounded-none" data-testid="account-change-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Change account number?</AlertDialogTitle>
            <AlertDialogDescription>
              You're changing the active account number from{' '}
              <span className="font-mono font-semibold">{confirmChange?.from}</span> to{' '}
              <span className="font-mono font-semibold">{confirmChange?.to}</span>.
              The previous value is kept in history and this change is recorded in the audit trail (who &amp; when). Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none"
              onClick={() => setForm((f) => ({ ...f, account_number: original?.account_number || '' }))}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none" onClick={doSave} data-testid="account-change-confirm-yes">Yes, change it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none" data-testid="delete-entity-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete identifier?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes identifier{' '}
              <span className="font-mono font-semibold">{deleteTarget?.identifier_number}</span>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={doDelete} data-testid="delete-entity-confirm-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit history */}
      <Dialog open={!!auditItem} onOpenChange={(o) => !o && setAuditItem(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="entity-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Audit — {auditItem?.identifier_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Account number history</h4>
              {(auditItem?.account_number_history || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No account-number changes recorded.</p>
              ) : (
                <ol className="space-y-2" data-testid="audit-account-history">
                  {[...(auditItem?.account_number_history || [])].reverse().map((h, i, arr) => (
                    <li key={i} className="flex items-center justify-between text-sm border-l-2 border-indigo-400 pl-3">
                      <span className="font-mono">{h.account_number || '(none)'}{i === 0 && <Badge className="ml-2 rounded-none text-[10px]">ACTIVE</Badge>}</span>
                      <span className="text-xs text-muted-foreground">{h.changed_by} · {new Date(h.changed_at).toLocaleString()}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Change log</h4>
              {(auditItem?.audit_log || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No changes recorded.</p>
              ) : (
                <ol className="space-y-2" data-testid="audit-log">
                  {[...(auditItem?.audit_log || [])].reverse().map((a, i) => (
                    <li key={i} className="text-sm border-b pb-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{a.action?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-muted-foreground">{a.by} · {new Date(a.ts).toLocaleString()}</span>
                      </div>
                      {(a.from || a.to) && (
                        <div className="text-xs text-muted-foreground font-mono">{a.from || '∅'} → {a.to || '∅'}</div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
